import { Injectable } from '@angular/core';
import { ApiService, GetRequestArguments, PostRequestArguments, Refresh } from './api.service';
import {BehaviorSubject, forkJoin} from 'rxjs';
import { UserService } from './user.service';
import { UntypedFormArray, UntypedFormBuilder, Validators } from '@angular/forms';
import { INDEX_TO_WEEK } from '../util/interfaces/Project';
import { FeedbackService } from './feedback.service';

@Injectable({
  providedIn: 'root'
})
export class ProjectService {
  /*
  Common User Fields
  */
  public $projects = new BehaviorSubject([]);
  public $defaultProject = new BehaviorSubject([]);
  /*
  Admin Feature Fields
  */
  public $allProjects = new BehaviorSubject<any>([]);

  public $editing = new BehaviorSubject<any>({});

  public $assign = new BehaviorSubject<any>({});

  public $new = new BehaviorSubject<any>({});

  public $timeCategories = new BehaviorSubject<any>([]);
  public $expenseCategories = new BehaviorSubject<any>([]);

  public $activities = new BehaviorSubject([]);

  private url = 'project';
  private groupKey = 'project';
  private hour = 60 * 60;
  constructor(private api: ApiService,
              private userService: UserService,
              private formBuilder: UntypedFormBuilder,
              private feedbackService: FeedbackService) {
    // this.loadAll();
  }

  loadAll(refresh: Refresh = 'expired') {
    return new Promise<void>((resolve, reject) => {
      forkJoin(
        [
          this.loadProjectsForAdmin(refresh),
          this.loadProjectsForUser(refresh),
          this.loadItemCategories(refresh),
          this.loadActivities(refresh),
        ]).subscribe(
        () => { resolve(); },
        error => { reject(); }
      );
    });
  }

  load(refresh: Refresh = 'expired') {
    this.loadProjectsForUser(refresh);
  }

  public loadActivities(refresh: Refresh = 'expired') {
    const args = new GetRequestArguments({
      url: [this.url, 'activities'],
      cacheGroup: this.groupKey,
      refresh,
      ttl: 60 * 60,
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe(res => {
      this.api.updateObservable(res, this.$activities);
    });
    return call;
  }

  public loadActivitiesForProject(refresh: Refresh = 'expired', projectId) {
    const args = new GetRequestArguments({
      url: [this.url, 'activities', projectId],
      cacheGroup: this.groupKey,
      refresh,
      ttl: 60 * 60,
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe(res => {
      this.api.updateObservable(res, this.$activities);
    });
    return call;
  }

  public post(project: FormData) {
    const args = new PostRequestArguments({
      url: this.url,
      object: project,
    });
    const call = this.api.postRequest(args);
    call.subscribe(res => {
      const index = this.$allProjects.value.findIndex(c => c.id === res['id']);
      if (index > -1) {
        this.$allProjects.value[index] = res;
        this.$editing.next(undefined);
      } else {
        this.$allProjects.value.push(res);
      }
      this.$allProjects.value.sort((a, b) => {
        if (a.active && !b.active) { return -1; }
        else if (b.active && !a.active) { return 1; }
        else {
          return a.name.localeCompare(b.name);
        }
      });
      this.api.clearCacheGroup(this.groupKey);
    });
    return call;
  }

  delete(project) {
    const args = new PostRequestArguments({
      url: this.url,
      object: project,
    });
    const call = this.api.deleteRequest(args);
    call.subscribe(
      id => {
        if (typeof id === 'string') {
          const index = this.$allProjects.value.findIndex(p => p.id === parseInt(id, 10));
          this.$allProjects.value.splice(index, 1);
        }
        this.feedbackService.message('Project was successfully deleted', 'Success');
      },
      error => {
        this.feedbackService.standardError(error);
      });
    return call;
  }

  public createProjectForm() {
    return this.formBuilder.group({
      active: [true, [Validators.required]],
      assignees: this.formBuilder.array([]),
      //budget: this.formBuilder.group({
      description: [null, []],
      end_date: [null, []],
      hours: [null, []],
      project_id: [null, []],
      start_date: [null, []],
      //}),
      client: [null, []],
      client_id: [null, [Validators.required]],
      id: [null, []],
      name: [null, [Validators.required]],
      tracking_target: [null, [Validators.required]],
      additional_data: [null, []],
      invoicing_address: [null, []],
      invoice_template_id: [null, []],
      invoicing_regional_format: [null, []],
      external_id: [null, []],
      timeCategories: [undefined, []],
      expenseCategories: [undefined, []],
      billing_currency_id: [undefined, []],
    });
  }

  public fillProjectForm(form, projectData) {
    if (projectData.active !== undefined) {
      form.controls.active.setValue(projectData.active);

      /*if (projectData.assignees) {
        for (const user of projectData.assignees) {
          const index = form.value.assignees.findIndex(a => a.user_id === user.user_id);

          if (index === -1) {
            const userForm = this.createAssigneeForm();
            this.fillAssigneeForm(userForm, user);
            (form.controls.assignees as UntypedFormArray).push(userForm);
          }
        }
      }*/
      if (projectData.assignees) {
        const assigneesArray = Array.isArray(projectData.assignees)
              ? projectData.assignees // Already an array
              : Object.values(projectData.assignees);


        for (const user of assigneesArray) {

          const index = form.value.assignees.findIndex(a => a.user_id === user.user_id);
          if (index === -1) {
            const userForm = this.createAssigneeForm();
            this.fillAssigneeForm(userForm, user);
            (form.controls.assignees as UntypedFormArray).push(userForm);
          }
        }
      }
      //form.controls.itemCategories.patchValue(projectData.item_categories);
      //form.controls.allow_weekends.setValue(projectData.allow_weekends);
      /*if (projectData.budget) {
        form.controls.budget.controls.start_date.setValue(projectData.budget.start_date);
        form.controls.budget.controls.project_id.setValue(projectData.budget.project_id);
        form.controls.budget.controls.end_date.setValue(projectData.budget.end_date);
        form.controls.budget.controls.hours.setValue(projectData.budget.hours);
        form.controls.budget.controls.description.setValue(projectData.budget.description);
      }*/
      form.controls.start_date.setValue(projectData.start_date);
      form.controls.project_id.setValue(projectData.project_id);
      form.controls.end_date.setValue(projectData.end_date);
      form.controls.hours.setValue(projectData.hours);
      form.controls.description.setValue(projectData.description);
      form.controls.client.setValue(projectData.client);
      form.controls.client_id.setValue(projectData.client?.id);
      form.controls.id.setValue(projectData.id);
      form.controls.name.setValue(projectData.name);
      form.controls.tracking_target.setValue(projectData.tracking_target);

      form.controls.invoicing_address.setValue(projectData.invoicing_address);
      form.controls.invoice_template_id.setValue(projectData.invoice_template_id);
      form.controls.invoicing_regional_format.setValue(projectData.invoicing_regional_format);
      form.controls.external_id.setValue(projectData.external_id);
      /*
       I need to subscribe to timeCategories, because I also need to retrieve the billable column, which I do not have in the itemCategories retrieved
       through the project from the backend (because it is a column in the additions table).
       Therefore I do a mapping between the itemCategory in the projectData (which are those selected) ad the one in the timeCategories BehaviourSubject
      */
      /*this.$timeCategories.subscribe(
        () => {
          const timeCategoryIds = projectData.item_categories
          .filter(category => category.item_type.name === 'time')
          .map(category => category.id);

          const filteredTimeCategories = this.$timeCategories.value
            .filter(timeCategory => timeCategoryIds.includes(timeCategory.id));

          form.controls.timeCategories.setValue(filteredTimeCategories);
        }
      );*/
      form.controls.timeCategories.setValue(projectData.item_categories.filter(category => category.item_type.name === 'time'));

      form.controls.expenseCategories.setValue(projectData.item_categories.filter(category => category.item_type.name === 'expense'));

      form.controls.billing_currency_id.setValue(projectData.billing_currency_id);
    }
  }

  public createAssigneeForm() {
    return this.formBuilder.group({
      id: [null, []],
      note: [null, []],
      project_id: [null, []],
      timeCategories: [undefined, []],
      expenseCategories: [undefined, []],

      //rate: [null, []],
      rate: this.formBuilder.array([]),
      role: [null, []],
      user: [null, []],
      user_id: [null, []],
      remote: [null, []],
    });
  }

  public fillAssigneeForm(form, data) {
    form.controls.note.setValue(data.note);
    form.controls.project_id.setValue(data.project_id);
    form.controls.timeCategories.patchValue(data.timeCategories);
    form.controls.expenseCategories.patchValue(data.expenseCategories);
    const rateFormArray = form.get('rate') as UntypedFormArray;
    rateFormArray.clear(); // Clear existing controls in case there are any

    if (data.rate && Array.isArray(data.rate)) {
      data.rate.forEach(rateItem => {
        const rateFormGroup = this.formBuilder.group({
          category_id: rateItem.category_id,
          rate: rateItem.rate,
        });
        rateFormArray.push(rateFormGroup);
      });
    }
    form.controls.remote.patchValue(data.remote);
    form.controls.role.setValue(data.role ? data.role : '');
    form.controls.user.setValue(data.user);
    form.controls.user_id.setValue(data.user_id);
  }

  public createFormData(project) {
    const formData = new FormData();
    for (const key of Object.keys(project)) {
      let value = project[key];
      if (value !== undefined && value !== null) {
        if (value instanceof Object) {
          value = JSON.stringify(value);
        }
        formData.append(key, value);
      }
    }
    return formData;
  }

  public loadProjectForEdit(projectId) {
    const args = new GetRequestArguments({
      url: [this.url, projectId],
      cacheGroup: undefined,
    });
    this.api.cachedGetRequest(args).subscribe(res => {
      const form = this.createProjectForm();
      this.fillProjectForm(form, res);
      this.api.updateObservable(form, this.$editing);
    });
  }

  public loadProjectsForUser(refresh: Refresh = 'expired') {
    const args = new GetRequestArguments({
      url: this.url,
      cacheGroup: this.groupKey,
      refresh,
      ttl: this.hour * 24,
      getAll: false,
      delayed: false,
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe(projects => {
      this.api.updateObservable(projects, this.$projects);
    });
    return call;
  }

  public loadProjectsForUserItemCategory(refresh: Refresh = 'expired', itemCategoryId) {
    const args = new GetRequestArguments({
      url: [this.url, 'userItemCategories', itemCategoryId],
      cacheGroup: this.groupKey,
      refresh,
      ttl: this.hour * 24,
      getAll: false,
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe(projects => {
      this.api.updateObservable(projects, this.$projects);
    });
    return call;
  }

  public loadProjectsForAdmin(refresh: Refresh = 'expired') {
    const args = new GetRequestArguments({
      url: this.url,
      cacheGroup: this.groupKey,
      refresh,
      ttl: this.hour * 24,
      getAll: true,
      delayed: false,
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe(projects => {
      this.api.updateObservable(projects.find(c => c.name === "Intern"), this.$defaultProject);
      this.api.updateObservable(projects, this.$allProjects);
    });
    return call;
  }

  public loadItemCategories(refresh: Refresh = 'expired') {
    const args = new GetRequestArguments({
      url: [this.url, 'itemCategories'],
      cacheGroup: this.groupKey,
      refresh,
      delayed: false,
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe(itemCategories => {
      this.api.updateObservable(itemCategories[0], this.$timeCategories);
      this.api.updateObservable(itemCategories[1], this.$expenseCategories);
    });
    return call;
  }

}
